import { stringSort } from "utils/defaultSort";

import { API_URL } from "General/APIEndpoints";
import { SubmitStore } from "Stores/RESTStoreInstances/SubmitStore";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { ArrayDataStore } from "Stores/RESTStoreInstances/ArrayDataStore";
import { API } from "General/API";

class ProjectStore extends ArrayDataStore<API_AdminProject> {
  constructor() {
    super();
    this.data = [] as ArrayDataStore<API_AdminProject>["data"];
  }
  url() {
    return API_URL.ADMIN_PROJECTS.getAllProjects();
  }
}

class ProjectList extends ProjectStore {
  activeProjects() {
    return this.data.filter((val: API_AdminProject) => {
      return val.status === "ENABLED";
    });
  }

  processResponse = (data: API_MultiAdminProjectResponse) => {
    return data.items;
  };

  /**
   * Used in Admin New/Edit Projectgroups
   *
   * @param projectGroupId
   */
  projectGroupAssignableProjects(projectGroupId?: number) {
    return this.activeProjects()
      .filter((val: API_AdminProject) => {
        if (projectGroupId) {
          // Edit Project Group
          return val.projectGroupId === undefined || val.projectGroupId === projectGroupId;
        } else {
          // New Project Group
          return val.projectGroupId === undefined;
        }
      })
      .sort((a, b) => stringSort(a, b, "name"));
  }

  //   /** DEPRECATED SINCE IT SHOULD BE HANDLED ON BACK-END
  //    * List of projects assignable to a user
  //    *
  //    * Note: It probably does not make sense to add a user to a
  //    * project that is not active. However at this time it is possible.
  //    * The user cannot see the project if it is assigned to him.
  //    *
  //    * @param projectGroupId the ProjectGroup Id of the User
  //    */
  //   userAssignableProjects(projectGroupId?: number) {
  //     if (projectGroupId) {
  //       return this.data.filter((value) => {
  //         return value.projectGroupId === projectGroupId;
  //       });
  //     } else {
  //       return this.data;
  //     }
  //   }
}

const projectList = new ProjectList();
export { projectList };

export class ProjectsSubmitStore extends SubmitStore<API_AdminProject, API_AdminProject, ProjectList> {
  infoData: API_AccessRightsInfo = null;
  constructor(dataStore) {
    super(dataStore);

    makeObservable(this, {
      infoData: observable,
      modules: computed,
      hasModules: computed,
      functionsOptions: computed,
      modulesOptions: computed,
      functions: computed,
      hasFunctions: computed,
      resetForAdditionalProject: action,
      getPresets: action.bound,
      submitProject: action.bound,
      setFolderStructure: action.bound,
      fetchInfo: action.bound,
    });
  }

  url(id?: number) {
    return id ? API_URL.ADMIN_PROJECTS.getProject(id) : API_URL.ADMIN_PROJECTS.getAllProjects();
  }

  get modules() {
    return this.data.modules ? (this.data.modules as { [key in API_MODULEDATA_MODULE]: boolean }) : undefined;
  }

  get hasModules() {
    return !!this.data.modules;
  }

  get functionsOptions() {
    return this.data.functionOptions ? (this.data.functionOptions as API_FunctionOptions) : undefined;
  }

  get modulesOptions() {
    return this.data.moduleOptions ? (this.data.moduleOptions as API_ModuleOptions) : undefined;
  }

  get functions() {
    return this.data.functions ? (this.data.functions as { [key in API_PROJECT_FUNCTIONS]: boolean }) : undefined;
  }

  get hasFunctions() {
    return !!this.data.functions;
  }

  get billing() {
    return this.data.billingInformation ? (this.data.billingInformation as API_BillingInformation) : undefined;
  }

  resetForAdditionalProject() {
    this.clear();
  }

  @handleStoreState()
  async submitProject(id?: number) {
    if (id) {
      return await this.put(id, this.data);
    } else {
      return await this.post(this.data);
    }
  }

  @handleStoreState()
  async editScheduledJobs(projectId: number, scheduledJobs: API_DeleteProject[]) {
    try {
      return await API.editData(API_URL.ADMIN_PROJECTS.updateProjectTimers(projectId), scheduledJobs);
    } catch (err) {
      console.warn(err);
      throw err;
    }
  }

  @handleStoreState()
  async setFolderStructure(projectId: number, structure: API_TreeElement | Array<API_TreeElement>) {
    try {
      return await API.newData(API_URL.FOLDERS.createFolders(projectId), structure);
    } catch (err) {
      console.warn(err);
      throw err;
    }
  }

  async getPresets(): Promise<{ [key in API_ADMINPROJECT_PRESET]: string }> {
    try {
      const response = await API.getData(API_URL.ADMIN_PROJECTS.getProjectPresets());
      return response;
    } catch (err) {
      console.warn(err);
      throw err;
    }
  }

  async fetchInfo(id: number) {
    try {
      const response = await API.getData(API_URL.REPORTS.getAccessRightsInfo(id));
      runInAction(() => {
        this.infoData = response;
      });
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }
}

export const projectSubmitStore = new ProjectsSubmitStore(projectList);
